import { Typography } from '@euler/app/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { AiHomeSuggestion } from '@euler/model/ai-home';
import { ChevronRight, RefreshCw, Sparkles } from 'lucide-react-native';
import { memo, useMemo } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { aiColors, aiElevation, aiSuiteToolForFeature, tint } from '../theme';
import type { AiHomeSuggestionsState } from './AiHomeView';

const kSkeletonWidths: Array<[`${number}%`, `${number}%`]> = [
  ['76%', '40%'],
  ['70%', '34%'],
  ['80%', '40%'],
  ['64%', '48%'],
];

export const AiQuickActions = memo(
  ({
    state,
    onPress,
    onRetry,
  }: {
    state: AiHomeSuggestionsState;
    onPress: (item: AiHomeSuggestion) => void;
    onRetry: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const styles = useStyles();
    return (
      <View>
        <View style={styles.header}>
          <Typography.Text bold size={rpx(16)} color={aiColors.foreground}>
            快速开始
          </Typography.Text>
        </View>
        {state.failed ? (
          <Pressable
            accessibilityRole="button"
            onPress={onRetry}
            style={[styles.row, styles.retry]}
          >
            <RefreshCw size={16} color={aiColors.mutedForeground} />
            <Typography.Text size={rpx(13)} color={aiColors.mutedForeground}>
              暂时无法加载，点击重试
            </Typography.Text>
          </Pressable>
        ) : state.items == null ? (
          kSkeletonWidths.map(([title, subtitle], index) => (
            <View key={index} style={styles.row} accessibilityLabel="加载中">
              <View style={[styles.icon, styles.skeleton]} />
              <View style={styles.text}>
                <View style={[styles.bar, { width: title }]} />
                <View
                  style={[styles.bar, styles.barSmall, { width: subtitle }]}
                />
              </View>
              <View style={styles.dot} />
            </View>
          ))
        ) : (
          // What is not available yet already shows in the tool grid.
          state.items
            .filter(item => item.available)
            .map(item => {
              const tool = aiSuiteToolForFeature(item.feature);
              const color = tool?.color ?? aiColors.primary;
              const Icon = tool?.icon ?? Sparkles;
              return (
                <Pressable
                  key={item.id}
                  accessibilityRole="button"
                  onPress={() => onPress(item)}
                  style={({ pressed }) => [
                    styles.row,
                    pressed && { opacity: 0.8 },
                  ]}
                >
                  <View
                    style={[
                      styles.icon,
                      { backgroundColor: tint(color, 0.15) },
                    ]}
                  >
                    <Icon size={18} color={color} />
                  </View>
                  <View style={styles.text}>
                    <Typography.Text
                      medium
                      size={rpx(14)}
                      color={aiColors.foreground}
                    >
                      {item.prompt}
                    </Typography.Text>
                    <Typography.Text
                      size={rpx(12)}
                      lineHeight={rpx(18)}
                      color={aiColors.mutedForeground}
                      numberOfLines={2}
                    >
                      {item.description}
                    </Typography.Text>
                  </View>
                  <ChevronRight size={16} color={aiColors.mutedForeground} />
                </Pressable>
              );
            })
        )}
      </View>
    );
  },
);

function useStyles() {
  const { rpx } = useResponsiveContext();
  return useMemo(
    () =>
      StyleSheet.create({
        header: {
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginTop: rpx(22),
          marginBottom: rpx(12),
        },
        row: {
          flexDirection: 'row',
          alignItems: 'center',
          gap: rpx(12),
          padding: rpx(12),
          marginBottom: rpx(10),
          borderRadius: rpx(14),
          borderWidth: 1,
          borderColor: aiColors.border,
          backgroundColor: aiColors.card,
          ...aiElevation.card,
        },
        retry: { justifyContent: 'center', gap: rpx(8) },
        icon: {
          width: rpx(36),
          height: rpx(36),
          borderRadius: rpx(10),
          alignItems: 'center',
          justifyContent: 'center',
        },
        skeleton: { backgroundColor: aiColors.skeleton },
        text: { flex: 1, gap: rpx(6) },
        bar: {
          height: rpx(12),
          borderRadius: rpx(6),
          backgroundColor: aiColors.skeleton,
        },
        barSmall: { height: rpx(9) },
        dot: {
          width: rpx(16),
          height: rpx(16),
          borderRadius: rpx(8),
          backgroundColor: aiColors.skeleton,
        },
      }),
    [rpx],
  );
}
